/**
 * 画面詳細。画面 1 枚と、そこに載るコンポーネントの全件を 1 画面に集約する（大枠設計 §7）。
 *
 * **読む先は要素の表（`spec_graph_*`）である。** 画面もコンポーネントも番号の ID を持ち、名前は語の
 * 組み合わせから導かれる。
 *
 * 入った直後に見せるのは、左上の型の札「画面」と画面名と、タブ「コンポーネント」の中の基本の欄（URL パターン・
 * 種類・ドメイン）と コンポーネント名 ／ 種別 ／ 操作結果 の 3 列の表にする（Dhik 2026-09-29「本当に出すべき情報を 1 or 2 個に
 * するぐらい情報を絞ってほしい」「画面詳細の基本欄は部品と統合して部品一覧の上に出してほしい」「詳細
 * ページの左上に出していたどのアウトプットかのタグは戻してほしい」）。この画面へ来る所はタブ「使われて
 * いる場所」へ移し、コンポーネントの画面仕様書としての項目（種別・表示条件・変数部の表示ロジック・操作結果）は
 * 行を押すと開く右パネルに畳まずに置く（Dhik 2026-09-29「一般的に画面仕様書として必要な情報はだしてくれ」
 * 「表だとゴチャつくから押下したときのサイドバーの情報といい感じに分散させてほしい」）。呼び名は見本の
 * 画面仕様書の列名をそのまま使う（同日「画面詳細の部品はコンポーネント名、押すとは操作結果にしてほしい。
 * 謎日本語使うな。」）。
 *
 * 深いリンクで存在しない番号を指された場合は `NotFoundState` に倒す。BFF が `NOT_FOUND` を
 * 返すのは存在しない番号のときだけなので、通信・サーバー障害（`ErrorState`）と区別できる。
 * 数字でないパスもここで受ける — 読取へ投げると入力の検証で弾かれ、URL の打ち間違いが
 * 「取得に失敗しました」として出る。
 *
 * 案件を選んでいるときは、同じ画面を変更前（`side: 'before'`）と変更後（`side: 'after'`）の
 * 2 回引き、タブ「コンポーネント」の中の基本の欄とコンポーネントの表をそれぞれ 欄 ／ 変更前 ／ 変更後 の 3 列に並べる（案件
 * モードの原則: 中身を見る画面は左に as-is、右に to-be）。コンポーネントの行を押すと右パネルが 2 列になる。
 * **案件で基本の欄もコンポーネントも 1 つも変わっていなければ 3 列にせず、案件なしと同じ通常の表示で
 * 描く**（`isPjUnchanged`）。右パネルから出るリンクは案件を持ったまま繋ぐ。
 *
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business 要素の表から読んだ画面 1 枚を、型の札と画面名とタブ（コンポーネント（上に基本の欄） ・ 使われている場所）と、コンポーネントの行を押すと開く右パネルで描き、案件を選べば差分があるときだけ基本の欄とコンポーネントの表を左右に並べる。
 * @graph-connects useScreenDetail [calls] 画面詳細の取得
 * @graph-connects buildScreenComponentRows [calls] コンポーネントの表の行と差分の導出
 * @graph-connects PjCompareTable [calls] 案件モードの 3 列の表
 */
import type { ScreenComponentRow, ScreenDetailOutput } from '@cortex/domain-spec-console';
import { Skeleton } from '@cortex/web-design-system/components/ui/Skeleton';
import { Link, useParams, useSearch } from '@tanstack/react-router';
import { useCallback, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import { DetailTabs } from '../components/spec/DetailTabs.js';
import { ErrorState, NotFoundState } from '../components/spec/fallback-states.js';
import { NodeTypeBadge } from '../components/spec/NodeTypeBadge.js';
import type { SpecDiffState } from '../components/spec/SpecDiffState.js';
import { displayLabel } from '../components/spec/UntranslatedBadge.js';
import { useScreenDetail } from '../hooks/queries/spec-graph-element-screens-queries.js';
import { isNotFoundError } from '../lib/orpc-errors.js';
import { elementPjNumberOf } from '../lib/pj-mode.js';
import { buildScreenComponentRows } from './element-screen-detail-diff.js';
import {
  buildPjCompareRows,
  isPjUnchanged,
  PjCompareTable,
  pjCompareRowState,
  type PjCompareRow,
  type PjSides,
} from './pj-compare-table.js';
import { usePjNodePresence } from './pj-master-presence.js';
import { pjLinkSearch } from './pj-link.js';
import { pjTextOf } from './pj-search.js';
import { SCREENS_ROUTE, SCREEN_DETAIL_ROUTE } from './route-paths.js';
import {
  ComponentName,
  componentKindText,
  OperationResultLines,
  ScreenComponentTable,
  ValueOrDash,
} from './screen-detail-cells.js';
import {
  ScreenComponentComparePanel,
  ScreenComponentPanel,
  useEscapeToClose,
} from './screen-detail-panel.js';
import {
  ScreenBasicTable,
  screenDetailTabs,
  ScreenIncomingList,
  type ScreenDetailTab,
} from './screen-detail-tabs.js';
import { untranslatedHint } from './screen-hints.js';

/** ローディング時に出すスケルトンの区画数。見出しとコンポーネントの表に揃える。 @graph-connects none */
const SKELETON_SECTIONS = 2;

/**
 * URL のパスから画面の番号を読む。1 以上の整数でなければ `null` を返す。
 *
 * 関門を「1 以上の整数」に置くのは契約（`ScreenElementIdSchema`）と同じ線である。`Number()` は
 * 空文字を 0 に、末尾の空白を無視して数に変えるので、綴りをそのまま確かめる。
 *
 * @param raw パスパラメータの綴り
 * @returns 画面の番号。1 以上の整数でなければ null
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business パスパラメータの綴りを、契約と同じ関門（1 以上の整数）で画面の番号へ読む。
 * @graph-connects none
 */
export function readScreenIdParam(raw: string): number | null {
  if (!/^\d+$/u.test(raw)) return null;
  const parsed = Number(raw);
  return parsed >= 1 ? parsed : null;
}

/**
 * 一覧へ戻るリンク。案件を持ったまま帰す。
 *
 * 落とすと、案件を選んで開いた利用者が案件の外れたマスターの一覧へ帰り、外れたことは画面に
 * 出ない。
 *
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business 画面詳細から画面一覧へ、案件を引き継いで戻るリンクを描く。
 * @graph-connects pjLinkSearch [calls] 戻るリンクへ案件を引き継ぐ検索条件
 */
function ScreenBackLink({ pj }: { pj?: string }) {
  const { t } = useTranslation();
  return (
    <Link
      to={SCREENS_ROUTE}
      {...pjLinkSearch(pj)}
      className="text-sm text-[var(--color-accent-primary)] underline"
    >
      {t('spec.backToList')}
    </Link>
  );
}

/** 取得中のスケルトン。 @graph-connects none */
function ScreenDetailSkeleton() {
  const { t } = useTranslation();
  return (
    <div className="flex flex-col gap-4" aria-busy="true" aria-label={t('common.loading')}>
      {Array.from({ length: SKELETON_SECTIONS }, (_, index) => (
        <Skeleton key={index} variant="rectangular" height="7rem" />
      ))}
    </div>
  );
}

/**
 * @graph-node {Function}
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business 画面詳細を、番号不正は未存在へ、案件モードなら左右比較（差分が無ければ 1 面）へ、それ以外は 1 面の詳細へ振り分ける。
 * @graph-connects readScreenIdParam [calls] パスパラメータから画面の番号を読む
 * @graph-connects ScreenDetailSingle [calls] 1 面の詳細
 * @graph-connects ScreenDetailCompare [calls] 案件モードの左右比較
 * @graph-connects pjTextOf [calls] 検索条件の案件番号を文字列へ戻す
 */
export function ScreenDetailPage() {
  const { screenId: rawScreenId } = useParams({ from: SCREEN_DETAIL_ROUTE });
  const pj = pjTextOf(useSearch({ from: SCREEN_DETAIL_ROUTE }).pj);
  const screenId = readScreenIdParam(rawScreenId);
  const pjNumber = elementPjNumberOf(pj);

  if (screenId === null) {
    return <NotFoundState id={rawScreenId} action={<ScreenBackLink pj={pj} />} />;
  }
  if (pjNumber === undefined) {
    return <ScreenDetailSingle rawScreenId={rawScreenId} screenId={screenId} />;
  }
  return (
    <ScreenDetailCompare
      rawScreenId={rawScreenId}
      screenId={screenId}
      pj={pj}
      pjNumber={pjNumber}
    />
  );
}

/**
 * 案件を選んでいないときの 1 面の詳細を、取得中・未存在・失敗・本体の 4 状態で描く。
 *
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business 公開中の画面詳細を取得し、取得中・未存在・失敗・本体の 4 状態で描く。
 * @graph-connects useScreenDetail [calls] 画面詳細の取得
 */
function ScreenDetailSingle({ rawScreenId, screenId }: { rawScreenId: string; screenId: number }) {
  const { data, isLoading, error } = useScreenDetail(screenId);

  if (isNotFoundError(error)) {
    return <NotFoundState id={rawScreenId} action={<ScreenBackLink />} />;
  }
  if (error !== null && error !== undefined) {
    return <ErrorState error={error} />;
  }
  if (isLoading || data === undefined) {
    return <ScreenDetailSkeleton />;
  }

  return <ScreenDetailBody key={data.screenId} detail={data} pj={undefined} />;
}

/** `ScreenDetailCompare` の props。 @graph-connects none */
interface ScreenDetailCompareProps {
  rawScreenId: string;
  screenId: number;
  pj?: string;
  pjNumber: number;
}

/**
 * 案件モードの画面詳細。変更前と変更後を引き、揃ってから左右に並べる（差分が無ければ 1 面）。
 *
 * 案件が足した画面は変更前を、消した画面は変更後を引かない。引くと必ず未存在で返る。
 * 片方だけで描くと「差分なしの表」が一瞬出てから差分つきに入れ替わるので、両方の答えが
 * 揃うまで待つ。
 *
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business 案件モードで画面の変更前と変更後を引き、揃ってから基本の欄とコンポーネントの表を左右比較（差分が無ければ 1 面の詳細）で描く。
 * @graph-connects usePjNodePresence [calls] 案件が足した・消した画面かの判定
 * @graph-connects useScreenDetail [calls] 変更前・変更後の取得
 */
function ScreenDetailCompare({ rawScreenId, screenId, pj, pjNumber }: ScreenDetailCompareProps) {
  const presence = usePjNodePresence(pjNumber, screenId);
  const before = useScreenDetail(screenId, pjNumber, 'before', presence.master === 'present');
  const after = useScreenDetail(screenId, pjNumber, 'after', presence.applied === 'present');

  const beforeAbsent = presence.master === 'absent' || isNotFoundError(before.error);
  const afterAbsent = presence.applied === 'absent' || isNotFoundError(after.error);
  if (beforeAbsent && afterAbsent) {
    return <NotFoundState id={rawScreenId} action={<ScreenBackLink pj={pj} />} />;
  }
  const failure = (beforeAbsent ? null : before.error) ?? (afterAbsent ? null : after.error);
  if (failure !== null && failure !== undefined) {
    return <ErrorState error={failure} />;
  }
  if ((!beforeAbsent && before.data === undefined) || (!afterAbsent && after.data === undefined)) {
    return <ScreenDetailSkeleton />;
  }

  return (
    <ScreenDetailCompareBody
      key={screenId}
      sides={{
        before: beforeAbsent ? null : (before.data ?? null),
        after: afterAbsent ? null : (after.data ?? null),
      }}
      pj={pj}
    />
  );
}

/**
 * 表と右パネルの 2 段組。右パネルが閉じていれば表だけを幅いっぱいに置く。
 *
 * @graph-node {Function}
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business コンポーネントの表と右パネルを、右パネルが開いているときだけ 2 段組にして並べる。
 * @graph-connects none
 */
function TableWithPanel({ panel, children }: { panel: ReactNode; children: ReactNode }) {
  return (
    <div
      className={`grid items-start gap-4 ${panel === null ? 'grid-cols-1' : 'grid-cols-1 lg:grid-cols-[minmax(0,1fr)_420px]'}`}
    >
      <div className="flex min-w-0 flex-col gap-3">{children}</div>
      {panel}
    </div>
  );
}

/** `ScreenDetailBody` の props。 @graph-connects none */
interface ScreenDetailBodyProps {
  detail: ScreenDetailOutput;
  /** 右パネルのリンクへ引き継ぐ案件番号。案件を選んでいなければ undefined。 */
  pj: string | undefined;
}

/**
 * 取得済みの画面詳細（案件なし、または案件で何も変わっていない画面）を、型の札と画面名とタブで描く。
 *
 * 既定のタブは「コンポーネント」。コンポーネントの表の上に基本の欄（URL パターン・種類・ドメイン）を置く。行を押すと
 * 右パネルでそのコンポーネントを開き、同じ行をもう一度押すか ✕ か Esc で閉じる。案件を選んでいれば、右パネルと
 * 使われている場所のリンクは案件を持ったまま繋ぐ。コンポーネントが 0 件ならタブの件数が「0」と言うので、コンポーネントの
 * 表も空の文も描かない（基本の欄は描く）。
 *
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business 取得済みの画面詳細を、型の札と画面名と、タブ コンポーネント（上に基本の欄（URL パターン・種類・ドメイン）、下にコンポーネントの表と行を押すと開く右パネル）・使われている場所（この画面へ来る所）で描く。
 * @graph-connects ScreenDetailHeader [calls] 見出し
 * @graph-connects screenDetailTabs [calls] タブの並び
 * @graph-connects DetailTabs [calls] タブ
 * @graph-connects ScreenComponentTable [calls] コンポーネントの表
 * @graph-connects ScreenComponentPanel [calls] コンポーネントの右パネル
 * @graph-connects ScreenBasicTable [calls] 基本の欄
 * @graph-connects ScreenIncomingList [calls] この画面へ来る所
 * @graph-connects useEscapeToClose [calls] Esc で閉じる
 */
function ScreenDetailBody({ detail, pj }: ScreenDetailBodyProps) {
  const { t } = useTranslation();
  const [tab, setTab] = useState<ScreenDetailTab>('components');
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const close = useCallback(() => {
    setSelectedId(null);
  }, []);
  useEscapeToClose(selectedId !== null, close);

  const selectedComponent =
    detail.components.find((component) => component.componentId === selectedId) ?? null;
  const panel =
    selectedComponent === null ? null : (
      <ScreenComponentPanel component={selectedComponent} pj={pj} onClose={close} />
    );

  return (
    <div className="flex min-h-0 flex-col gap-4 overflow-y-auto">
      <ScreenDetailHeader detail={detail} />
      <DetailTabs
        tabs={screenDetailTabs(
          { componentCount: detail.components.length, incomingCount: detail.incoming.length },
          t,
        )}
        value={tab}
        onChange={setTab}
      />
      {tab === 'components' ? (
        <TableWithPanel panel={panel}>
          <ScreenBasicTable detail={detail} />
          {detail.components.length > 0 && (
            <ScreenComponentTable
              components={detail.components}
              selectedId={selectedComponent?.componentId ?? null}
              onSelect={(componentId) => {
                setSelectedId((current) => (current === componentId ? null : componentId));
              }}
            />
          )}
        </TableWithPanel>
      ) : (
        <ScreenIncomingList incoming={detail.incoming} pj={pj} />
      )}
    </div>
  );
}

/**
 * 見出し。左上に型の札「画面」を置き、その右に画面名を出す。選んだ言語の訳が欠けていれば名前を
 * 指すと「未翻訳」と出す。
 *
 * 型の札はほかの詳細（業務 ・ 業務ルール ・ ステージ ・ マスター ・ 用語）と同じ位置に置く。無いと、
 * どの成果物の詳細を見ているかが見出しから読めない（Dhik 2026-09-29「流石にないと迷子になる」）。
 *
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business 型の札「画面」と画面名を見出しにし、訳の欠けは名前を指すと出す文字に畳む。
 * @graph-connects NodeTypeBadge [calls] 型の札
 * @graph-connects untranslatedHint [calls] 未翻訳の印の文字
 */
function ScreenDetailHeader({ detail }: { detail: ScreenDetailOutput }) {
  const { t } = useTranslation();
  return (
    <header className="flex flex-wrap items-center gap-2" data-id={detail.screenId}>
      <NodeTypeBadge nodeType="Screen" />
      <h1
        className="text-2xl font-bold text-[var(--color-text-primary)]"
        title={untranslatedHint(detail.label, detail.untranslated, t) ?? undefined}
      >
        {displayLabel(detail.label, t, detail.untranslated)}
      </h1>
    </header>
  );
}

/** `ScreenDetailCompareBody` の props。 @graph-connects none */
interface ScreenDetailCompareBodyProps {
  sides: PjSides<ScreenDetailOutput>;
  pj?: string;
}

/**
 * 案件モードの本体。案件なしと同じ 2 つのタブを並べ、タブ「コンポーネント」の中の基本の欄（URL パターン・
 * 種類・ドメイン。名前は変わったときだけ）の表と、その下のコンポーネントの表を、それぞれ 欄 ／ 変更前 ／ 変更後 の
 * 3 列にする。変わっていない欄・コンポーネントも両側に薄い字で全部出す。基本の欄かコンポーネントが変わっていればコンポーネントの
 * タブに印を付ける。コンポーネントの行を押すと右パネルが 変更前 ／ 変更後 の 2 列になる。追加 ・ 変更 ・ 削除 は
 * 地色 ・ 斜線 ・ 取り消し線で分かるので、コンポーネント名を指すと出す文字にだけ載せる。使われている場所は変更後
 * （無ければ変更前）の姿の来る所を、案件を持ったまま繋ぐ。基本の欄もコンポーネントも 1 つも変わっていなければ
 * 3 列にせず、案件を持ったまま 1 面の詳細（`ScreenDetailBody`）で描く。
 *
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business 案件モードの画面詳細を、差分があればタブ コンポーネント の中で基本の欄とコンポーネントの表の左右比較と行を押すと開く 2 列の右パネルと、使われている場所で描き、差分が無ければ案件を持ったまま 1 面の詳細で描く。
 * @graph-connects buildPjCompareRows [calls] 基本の欄の左右の行
 * @graph-connects buildScreenComponentRows [calls] コンポーネントの変更前と変更後の突き合わせ
 * @graph-connects isPjUnchanged [calls] 差分が 1 つも無いかの判定
 * @graph-connects ScreenDetailBody [calls] 差分が無いときの 1 面の詳細
 * @graph-connects screenDetailTabs [calls] タブの並び
 * @graph-connects DetailTabs [calls] タブ
 * @graph-connects PjCompareTable [calls] 3 列の表
 * @graph-connects ScreenComponentComparePanel [calls] 2 列の右パネル
 * @graph-connects ScreenIncomingList [calls] この画面へ来る所
 * @graph-connects useEscapeToClose [calls] Esc で閉じる
 */
function ScreenDetailCompareBody({ sides, pj }: ScreenDetailCompareBodyProps) {
  const { t } = useTranslation();
  const [tab, setTab] = useState<ScreenDetailTab>('components');
  const [selectedKey, setSelectedKey] = useState<string | null>(null);
  const close = useCallback(() => {
    setSelectedKey(null);
  }, []);
  useEscapeToClose(selectedKey !== null, close);

  const shown = sides.after ?? sides.before;
  /* 左右の片方は必ず在る（両方無ければ呼び出し側が未存在へ倒す）。 */
  if (shown === null) return null;

  const labelRow = buildPjCompareRows<ScreenDetailOutput>(sides, [
    {
      key: 'label',
      label: t('screenDetail.fieldLabel'),
      compareKey: (detail) => detail.label,
      render: (detail) => displayLabel(detail.label, t, detail.untranslated),
    },
  ]);
  const basicRows = [
    /* 名前は見出しに出るので、変わったときだけ欄にする。 */
    ...labelRow.filter((row) => pjCompareRowState(row) === 'updated'),
    ...buildPjCompareRows<ScreenDetailOutput>(sides, [
      {
        key: 'pathPattern',
        label: t('screenDetail.fieldPathPattern'),
        compareKey: (detail) => detail.pathPattern ?? '',
        render: (detail) => (
          <span className="font-mono">
            {detail.pathPattern ?? t('screenDetail.pathPatternEmpty')}
          </span>
        ),
      },
      {
        key: 'kind',
        label: t('screenDetail.fieldKind'),
        compareKey: (detail) => detail.kind ?? '',
        render: (detail) => (
          <ValueOrDash value={detail.kind === null ? null : t(`screens.kind.${detail.kind}`)} />
        ),
      },
      {
        key: 'domain',
        label: t('screenDetail.fieldDomain'),
        compareKey: (detail) => detail.domain?.host ?? '',
        render: (detail) => (
          <span title={detail.domain?.host}>
            <ValueOrDash value={detail.domain?.label ?? null} />
          </span>
        ),
      },
    ]),
  ];

  const diffRows = buildScreenComponentRows(
    sides.after?.components ?? [],
    sides.before === null ? [] : sides.before.components,
  );
  const componentRows: PjCompareRow[] = diffRows.map((row) => ({
    key: String(row.component.componentId),
    label: <ScreenComponentName component={row.component} state={row.state} link={false} />,
    before: row.previous === null ? null : <ScreenComponentSummary component={row.previous} />,
    after: row.state === 'deleted' ? null : <ScreenComponentSummary component={row.component} />,
    changed: row.state === 'updated',
  }));
  if (isPjUnchanged(sides, [...basicRows, ...componentRows])) {
    return <ScreenDetailBody detail={shown} pj={pj} />;
  }

  const selectedRow = diffRows.find((row) => String(row.component.componentId) === selectedKey);
  const panel =
    selectedRow === undefined ? null : (
      <ScreenComponentComparePanel
        before={selectedRow.previous}
        after={selectedRow.state === 'deleted' ? null : selectedRow.component}
        title={
          <ScreenComponentName component={selectedRow.component} state={selectedRow.state} link />
        }
        onClose={close}
      />
    );
  const hasChange = (rows: readonly PjCompareRow[]) =>
    rows.some((row) => pjCompareRowState(row) !== 'unchanged');

  return (
    <div className="flex min-h-0 flex-col gap-4 overflow-y-auto">
      <ScreenDetailHeader detail={shown} />
      <DetailTabs
        tabs={screenDetailTabs(
          {
            componentCount: componentRows.length,
            incomingCount: shown.incoming.length,
            componentsChanged: hasChange(basicRows) || hasChange(componentRows),
          },
          t,
        )}
        value={tab}
        onChange={setTab}
      />
      {tab === 'components' ? (
        <TableWithPanel panel={panel}>
          <PjCompareTable firstColumn={t('pjCompare.fieldColumn')} rows={basicRows} />
          {componentRows.length > 0 && (
            <PjCompareTable
              firstColumn={t('pjCompare.componentColumn')}
              rows={componentRows}
              selectedKey={selectedKey}
              onSelectRow={(key) => {
                setSelectedKey((current) => (current === key ? null : key));
              }}
            />
          )}
        </TableWithPanel>
      ) : (
        <ScreenIncomingList incoming={shown.incoming} pj={pj} />
      )}
    </div>
  );
}

/**
 * コンポーネント名を「<用語><種別>」で描く。案件で 追加 ・ 変更 ・ 削除 したコンポーネントは、その札の
 * 文字を名前を指すと出す文字にだけ載せる（表と右パネルでは地色 ・ 斜線 ・ 取り消し線で分かる）。
 *
 * 表の行の名前は行を開くボタンの中に居るので用語をリンクにせず、右パネルの見出しでリンクにする。
 *
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business コンポーネント名を用語と種別で描き、右パネルの見出しでは用語を用語詳細へ繋ぎ、案件での追加・変更・削除は名前を指すと出す文字に畳む。
 * @graph-connects ComponentName [calls] 用語と種別のコンポーネント名
 */
function ScreenComponentName({
  component,
  state,
  link,
}: {
  component: ScreenComponentRow;
  state: SpecDiffState;
  /** 用語の部分を用語詳細へのリンクにするか。ボタンの中では false。 */
  link: boolean;
}) {
  const { t } = useTranslation();
  return (
    <span
      data-id={component.componentId}
      data-diff-state={state}
      title={state === 'unchanged' ? undefined : t(`specDiff.${state}`)}
    >
      <ComponentName component={component} link={link} />
    </span>
  );
}

/**
 * コンポーネント 1 件の片側の中身を 1 セルに描く。案件なしの表の 種別 ／ 操作結果 を、種別の下に操作結果の行を
 * 並べる形で 1 セルに収め、種別か操作結果が変われば行ごと変更の地色になる。リンクは置かない。
 *
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business コンポーネント 1 件の種別と操作結果を左右比較の 1 セルに並べて描く。
 * @graph-connects componentKindText [calls] 種別
 * @graph-connects OperationResultLines [calls] 操作結果
 */
function ScreenComponentSummary({ component }: { component: ScreenComponentRow }) {
  const { t } = useTranslation();
  return (
    <span className="text-xs">
      <span data-cell="kind" className="block text-[var(--color-text-secondary)]">
        <ValueOrDash value={componentKindText(component, t)} />
      </span>
      <span data-cell="result" className="block">
        <OperationResultLines component={component} />
      </span>
    </span>
  );
}
